import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CustomButton,
  CustomTextInput,
  CustomModal,
  CustomLoader,
  CustomMRTTable,
} from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/addon-configuration";
const ADDON_VIEW_API = "/saas/view/add-ons";
const ADDON_UPDATE_API = "/saas/update/add-ons";

const isSuccess = (response) => response?.data?.status === true;

const getUserId = () => {
  const user = TokenService.getUser();
  return user?.id || user?.user_id || 1;
};

const WarningIcon = () => (
  <svg
    width="56" height="56"
    viewBox="0 0 24 24" fill="none"
    stroke="#F5821F" strokeWidth="1.4" strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="7.5" x2="12" y2="13" />
    <circle cx="12" cy="16.3" r="0.9" fill="#F5821F" stroke="none" />
  </svg>
);

const RANGE_KEYS = ["range1", "range2", "range3", "range4"];

// ── Number input guards ───────────────────────────────────────────────────────
// Number input guards
const ALLOWED_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "Enter",
]);

const handleNumberKeyDown = (e) => {
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())
  ) {
    return;
  }

  if (ALLOWED_KEYS.has(e.key)) return;

  if (/^\d$/.test(e.key)) {
    const input = e.target;
    const currentValue = input.value || "";
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;

    // Don't allow 0 as the first character
    if (
      e.key === "0" &&
      currentValue === "" &&
      selectionStart === 0
    ) {
      e.preventDefault();
      return;
    }

    // If current value is "0", replace it when typing 1-9
    if (
      currentValue === "0" &&
      selectionStart === 1 &&
      selectionEnd === 1 &&
      e.key !== "0"
    ) {
      e.preventDefault();

      input.value = e.key;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      return;
    }

    return;
  }

  e.preventDefault();
};

const handleNumberWheel = (e) => {
  e.target.blur();
};


const CreateAddonConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const slabId = location.state?.slabId;

  const [rangeHeaders, setRangeHeaders] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusModal, setStatusModal] = useState({ open: false, type: "success", message: "" });
  const [errors, setErrors] = useState({});

  const errorsRef = useRef(errors);
  useEffect(() => { errorsRef.current = errors; }, [errors]);

  // ── Fetch view data ───────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!slabId) {
      setLoadError("No slab selected. Please go back and select a slab.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const res = await AxiosInstance.post(ADDON_VIEW_API, {
        slab_configuration_id: slabId,
      });

      if (!isSuccess(res) || !res.data?.data) {
        throw new Error(res?.data?.message || "Failed to load add-on pricing");
      }

      const { slab, modules } = res.data.data;

      const headers = RANGE_KEYS
        .filter((k) => slab?.[k] != null)
        .map((k) => ({ key: k, limit: slab[k] }));
      setRangeHeaders(headers);

      const rows = (modules || []).map((m) => ({
        pricingId: m.pricing_id ?? "",
        moduleId: m.module_id,
        featureLabel: m.module_name,
        range1: m.price_range1 ? String(m.price_range1) : "",
        range2: m.price_range2 ? String(m.price_range2) : "",
        range3: m.price_range3 ? String(m.price_range3) : "",
        range4: m.price_range4 ? String(m.price_range4) : "",
        canEdit: m.can_edit,
        statusLabel: m.status_label,
      }));
      setPricingRows(rows);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message || err?.message || "Error loading pricing configuration",
      );
      setRangeHeaders([]);
      setPricingRows([]);
    } finally {
      setLoading(false);
    }
  }, [slabId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePriceChange = useCallback((moduleId, rangeKey, value) => {
    const sanitized = value.replace(/[^0-9]/g, "").slice(0, 8);

    setPricingRows((prev) =>
      prev.map((row) =>
        row.moduleId === moduleId
          ? { ...row, [rangeKey]: sanitized }
          : row
      )
    );

    const errorKey = `${moduleId}-${rangeKey}`;
    if (errorsRef.current[errorKey]) {
      const next = { ...errorsRef.current };
      delete next[errorKey];
      errorsRef.current = next;      // ✅ sync — error clears on the same render
      setErrors(next);
    }
  }, []);
  useEffect(() => { errorsRef.current = errors; }, [errors]);
  const columns = useMemo(
    () => [
      {
        accessorKey: "featureLabel",
        header: "Plan Features",
        size: 220,
        enableSorting: false,
        enableColumnFilter: false,
      },
      ...rangeHeaders.map((rh) => ({
        accessorKey: rh.key,
        header: `Up to ${Number(rh.limit).toLocaleString()} users`,
        size: 180,
        enableColumnFilter: false,
        // ✅ Remove tabIndex from the <td> so Tab skips the cell wrapper
        //    and lands directly on the input inside
        muiTableBodyCellProps: {
          tabIndex: -1,
          sx: {
            outline: "none !important",
            "&:focus, &:focus-within, &:focus-visible": {
              outline: "none !important",
              boxShadow: "none !important",
            },
          },
        },
        Cell: ({ row }) => {
          const errorKey = `${row.original.moduleId}-${rh.key}`;
          return (
            <CustomTextInput
              key={errorKey}
              name={errorKey}
              type="number"
              size="sm"
              value={row.original[rh.key] ?? ""}
              onChange={(e) =>
                handlePriceChange(row.original.moduleId, rh.key, e.target.value)
              }
              onKeyDown={handleNumberKeyDown}
              onWheel={handleNumberWheel}
              placeholder="0"
              error={errorsRef.current[errorKey]}
            />
          );
        },
      })),
    ],
    [rangeHeaders, handlePriceChange],
  );

  // ── Validation — every price cell must be filled and > 0 ─────────────────
  const validate = () => {
    const newErrors = {};
    pricingRows.forEach((row) => {
      rangeHeaders.forEach((rh) => {
        const val = Number(row[rh.key] ?? 0);
        if (!val || val <= 0) {
          newErrors[`${row.moduleId}-${rh.key}`] = "Required";
        }
      });
    });
    errorsRef.current = newErrors;   // ✅ sync — Cell reads fresh ref on next render
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit handlers ───────────────────────────────────────────────────────
  const handleUpdateClick = () => {
    if (!validate()) return;
    setConfirmOpen(true);
  };

  const handleConfirmUpdate = async () => {
    setSubmitting(true);
    try {
      const payload = {
        slab_id: slabId,
        updated_by: getUserId(),
        created_by: getUserId(),
        data: pricingRows.map((row) => ({
          pricing_id: row.pricingId,
          module_id: row.moduleId,
          price_range1: Number(row.range1 || 0),
          price_range2: Number(row.range2 || 0),
          price_range3: Number(row.range3 || 0),
          price_range4: Number(row.range4 || 0),
        })),
      };

      const response = await AxiosInstance.put(ADDON_UPDATE_API, payload);

      if (isSuccess(response)) {
        setConfirmOpen(false);
        setStatusModal({
          open: true, type: "success",
          message: "Add-on pricing updated successfully",
        });
      } else {
        throw new Error(
          response?.data?.message || "Failed to update add-on pricing",
        );
      }
    } catch (err) {
      setConfirmOpen(false);
      setStatusModal({
        open: true, type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error updating add-on pricing",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusOk = () => {
    const wasSuccess = statusModal.type === "success";
    setStatusModal((prev) => ({ ...prev, open: false }));
    if (wasSuccess) navigate(LIST_PATH);
  };

  if (loading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading pricing configuration..." />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Edit Add-on Configuration</h1>
        </div>
      </div>

      {loadError && <p style={{ color: "#E23B3B", margin: "12px 0" }}>{loadError}</p>}

      <div className={styles.card}>
        {/* ✅ Wrap in class so we can kill the number spinners with CSS */}
        <div className={styles.noNumberSpinners}>
          <CustomMRTTable
            columns={columns}
            data={pricingRows}
            manualPagination={false}
            enablePagination={false}
            enableSorting={false}
            enableSearch={false}
            enableColumnFilters={false}
            enableColumnVisibility={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enableColumnActions={false}
            enableStickyHeader
          />
        </div>
      </div>

      <div className={styles.formActions}>
        <CustomButton
          variant="outline-dark"
          fullWidth={false}
          onClick={() => navigate(LIST_PATH)}
          disabled={submitting}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variant="primary"
          fullWidth={false}
          className={styles.btnOrange}
          onClick={handleUpdateClick}
          disabled={pricingRows.length === 0 || submitting}
        >
          Update
        </CustomButton>
      </div>

      <CustomModal
        isOpen={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        title=""
        size="md"
        showCloseButton={false}
      >
        <div style={{ textAlign: "center", padding: "32px 24px 8px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <WarningIcon />
          </div>
          <h2 style={{ margin: "0 0 16px", fontSize: "26px", fontWeight: 700, color: "#1A1A2E", letterSpacing: "-0.3px" }}>
            Are You Sure?
          </h2>
          <p style={{ color: "#797979", fontSize: "14px", lineHeight: "1.65", margin: "0 auto 28px", textAlign: "center" }}>
            You are about to save changes to the configuration add-ons pricing across all user slabs. Do you want to proceed?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", paddingBottom: "8px" }}>
            <CustomButton
              variant="outline-dark"
              fullWidth={false}
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              onClick={handleConfirmUpdate}
              loading={submitting}
              loadingText="Updating..."
            >
              Update Pricing
            </CustomButton>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={statusModal.open}
        onClose={handleStatusOk}
        showCloseButton={false}
        title={statusModal.type === "success" ? "Success" : "Failed"}
        size="sm"
      >
        <p className={styles.confirmText}>{statusModal.message}</p>
        <div className={styles.confirmActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={handleStatusOk}
          >
            OK
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default CreateAddonConfig;
